import React from 'react'
import lang from '@/lang'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'

import { searchResultTypes } from '@/modules/base/util/constants'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import { formatDate } from '@/modules/_helpers/formatDate'

import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import ArrowRightIcon from '@assets/images/icons/arr_right.svg'
import ArticleIcon from '@assets/images/icons/article.svg'
import CustomerIcon from '@assets/images/icons/customer.svg'
import DeliveryNoteIcon from '@assets/images/icons/deliveryNote.svg'
import ExpenseIcon from '@assets/images/icons/expense.svg'
import InvoiceIcon from '@assets/images/icons/invoice2.svg'
import OfferIcon from '@assets/images/icons/offer.svg'
import OrderIcon from '@assets/images/icons/order.svg'
import ProjectIcon from '@assets/images/icons/abschlag.svg'
import RecurringInvoiceIcon from '@assets/images/icons/recurringInvoice.svg'
import RevenueIcon from '@assets/images/icons/revenue.svg'
import SupplierIcon from '@assets/images/icons/deliveryNote2.svg'
import TimeTrackingIcon from '@assets/images/icons/timetracking.svg'
import LetterIcon from '@assets/images/icons/email.svg'
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded'
import { dispatch } from '@/redux/ducks/lists'
import { toggleSearchOverlay } from '@/redux/ducks/menu/main'
import { useNavigate } from 'react-router'

const icons = new Map([
  [searchResultTypes.ARTICLE, ArticleIcon],
  [searchResultTypes.CANCELLATION, InvoiceIcon],
  [searchResultTypes.CREDIT_NOTE, InvoiceIcon],
  [searchResultTypes.CUSTOMER, CustomerIcon],
  [searchResultTypes.DELIVERYNOTE, DeliveryNoteIcon],
  [searchResultTypes.DUNNING, InvoiceIcon],
  [searchResultTypes.EXPENSE, ExpenseIcon],
  [searchResultTypes.INVOICE, InvoiceIcon],
  [searchResultTypes.OFFER, OfferIcon],
  [searchResultTypes.ORDER, OrderIcon],
  [searchResultTypes.PROJECT, ProjectIcon],
  [searchResultTypes.RECURRINGINVOICE, RecurringInvoiceIcon],
  [searchResultTypes.REVENUE, RevenueIcon],
  [searchResultTypes.SUPPLIER, SupplierIcon],
  [searchResultTypes.TRACKEDTIME, TimeTrackingIcon],
  [searchResultTypes.LETTER, LetterIcon],
  [searchResultTypes.PURCHASE, ShoppingCartRoundedIcon],
])

const useStyles = makeStyles(theme => ({
  resultItemWrapper: {
    cursor: 'pointer',
    backgroundColor: theme.mode.backgroundColor.medium,
    '&:last-child $resultItem': {
      borderBottom: 'none',
    },
    '&:hover': {
      background: theme.mode.backgroundColor.light,
      '& $resultItem': {
        borderColor: theme.rowHoverColor,
      },
    },
  },
  resultItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '25px 30px',
    borderBottom: theme.border,
    gap: 20,
  },
  categoryIcon: {
    width: 'fit-content',
    '& > *': {
      fill: theme.mode.color.default,
    },
  },
  arrowIcon: {
    width: 'fit-content',
    '& > *': {
      fill: theme.mode.color.default,
    },
  },
  contentSegment: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    justifyContent: 'center',
    flex: '1 1 auto',
    width: '50%',
    overflow: 'hidden',
  },
  textRight: {
    textAlign: 'right',
  },
  truncate: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  smallText: {
    color: theme.mode.color.medium,
    fontSize: theme.fontSizes.smaller,
    height: 16,
  },
  title: {
    color: theme.mode.color.default,
  },
  additionalText: {
    color: theme.mode.color.default,
    fontWeight: theme.fontWeights.medium,
  },
}))

const SearchResultItem = ({ result }) => {
  const classes = useStyles()
  const navigate = useNavigate()
  const { type } = result
  let title, subtitle, additionalData1, additionalData2, additionalDataWithoutBold
  let url = `${type}/${result.id}`

  switch (type) {
    case searchResultTypes.ARTICLE:
      title = result.title
      subtitle = lang.globalSearch.numbers[type].replace('{{number}}', result.number) + (result.category ? ` (${result.category})` : '')
      additionalData1 = formatCurrency(result.price)
      break
    case searchResultTypes.CUSTOMER:
      title = result.companyName || (result.firstName ? `${result.firstName} ${result.lastName}` : result.lastName)
      subtitle = lang.globalSearch.numbers[type].replace('{{number}}', result.number)
      additionalData1 = result.email
      additionalData2 = (result.phone1 || result.phone2) && `${lang.globalSearch.phone} ${result.phone1 || result.phone2}`
      additionalDataWithoutBold = true
      break
    case searchResultTypes.DELIVERYNOTE:
      title = lang.globalSearch.numbers[type].replace('{{number}}', result.number)
      subtitle = result.customerData?.name
      additionalData1 = formatDate(result.date)
      url = `deliverynote/${result.id}`
      break
    case searchResultTypes.DUNNING:
      title = lang.globalSearch.numbers[type].replace('{{number}}', result.positions[0]?.number)
      subtitle = result.customerData?.name
      additionalData1 = formatCurrency(result.positions[0]?.total) + (result.charge && result.charge > 0 ? ` (+ ${formatCurrency(result.charge)})` : '')
      additionalData2 = formatDate(result.date)
      url = `dunning/${result.positions[0]?.invoiceId}/${result.id}`
      break
    case searchResultTypes.EXPENSE:
      title = result.payee
      subtitle = lang.globalSearch.expenseStates[result.state] || ''
      additionalData1 = formatCurrency(result.priceTotal)
      additionalData2 = formatDate(result.date)
      url = `expense/edit/${result.id}`
      break
    case searchResultTypes.CANCELLATION:
    case searchResultTypes.CREDIT_NOTE:
    case searchResultTypes.INVOICE:
    case searchResultTypes.ORDER:
    case searchResultTypes.OFFER:
      title = result.number === lang.draft ? result.number : lang.globalSearch.numbers[type].replace('{{number}}', result.number)
      subtitle = result.customerData?.name
      additionalData1 = formatCurrency(result.totalGross)
      additionalData2 = formatDate(result.date)
      break
    case searchResultTypes.PROJECT:
      title = result.title
      subtitle = result.customer?.name
      additionalData1 = formatCurrency(result.outstandingBudget)
      additionalData2 = formatDate(result.startDate)
      break
    case searchResultTypes.RECURRINGINVOICE:
      title = result.invoice?.customerData?.name
      subtitle = result.recurrence
      additionalData1 = formatCurrency(result.invoice?.totalGross)
      additionalData2 = formatDate(result.startDate)
      break
    case searchResultTypes.REVENUE:
      title = result.sender
      subtitle = lang.payKind[result.payKind] || ''
      additionalData1 = formatCurrency(result.priceTotal)
      additionalData2 = formatDate(result.date)
      url = `revenue/edit/${result.id}`
      break
    case searchResultTypes.SUPPLIER:
      title = result.name
      subtitle = lang.globalSearch.numbers[type].replace('{{number}}', result.number)
      additionalData1 = result.email
      additionalData2 = (result.phone1 || result.phone2) && `${lang.globalSearch.phone} ${result.phone1 || result.phone2}`
      additionalDataWithoutBold = true
      url = `supplier/edit/${result.id}`
      break
    case searchResultTypes.TRACKEDTIME:
      title = result.customer?.name
      subtitle = result.customer?.number || lang.globalSearch.numbers[type].replace('{{number}}', result.customer?.number)
      additionalData1 = formatCurrency(result.priceTotal)
      additionalData2 = result.durationInHours
      url = `timetracking/billing/customer/${result.customer.id}`
      break
    case searchResultTypes.LETTER:
      title = result.title
      subtitle = result.customerData?.name
      additionalData1 = formatDate(result.date)
      additionalData2 = result.customerData?.email
      break
    case searchResultTypes.PURCHASE:
      title = lang.globalSearch.numbers[type].replace('{{number}}', result.number)
      subtitle = result.supplierData?.name
      additionalData1 = formatCurrency(result.totalGross)
      additionalData2 = formatDate(result.date)
      break
  }

  const openResult = () => {
    navigate(url.toLowerCase())
    dispatch(toggleSearchOverlay(false))
  }

  return (
    <div className={classes.resultItemWrapper} onClick={openResult}>
      <div className={classes.resultItem}>
        <div><SVGIcon className={classes.categoryIcon} height='18px' width='18px' svg={icons.get(type)} /></div>
        <div className={classes.contentSegment}>
          <div className={clsx(classes.truncate, classes.smallText)}>{subtitle}</div>
          <div className={clsx(classes.truncate, classes.title)}>{title}</div>
        </div>
        <div className={clsx(classes.contentSegment, classes.textRight)}>
          <div className={clsx(classes.truncate, classes.smallText)}>{additionalData2}</div>
          <div className={clsx(classes.truncate, !additionalDataWithoutBold && classes.additionalText)}>
            {additionalData1}
          </div>
        </div>
        <div><SVGIcon className={classes.arrowIcon} height='12px' svg={ArrowRightIcon} /></div>
      </div>
    </div>
  )
}

export default SearchResultItem
